/**
 * React context for phase views extracted from `App.tsx` (roadmap §11.1).
 * {@link AppContextValue} lists stable handlers and home-screen summaries passed into `AuthView`, Options, etc.
 */
import React from 'react';
import type { AppPhase } from '../types';
import type {
  HomeScheduleHighlight,
  HomeTopPriorityAction,
  HomeUrgentAlert,
  HomeWeekInsights,
} from '../lib/buildHomeScreen';
import type { HomeShortcutKey } from '../lib/homeShortcutUsage';

export interface HomeSummary {
  todayShortLabel: string;
  lesson?: {
    title: string;
    course?: string;
    timeLabel?: string;
    /** Deep-link into Schedule (Agenda) for this occurrence. */
    scheduleItemId?: string;
    scheduleDate?: string;
  };
  assignmentsToGrade: number;
  assignmentsPrimaryLabel?: string;
  assignmentsSecondaryLabel?: string;
  parentsToContact: number;
  parentsLabel?: string;
  upcoming?: {
    title: string;
    whenLabel: string;
  };
  /** Next schedule rows for the home “upcoming” section (see buildHomeScheduleHighlights). */
  scheduleHighlights: HomeScheduleHighlight[];
}

/** Shared app state and handlers used by phase views. Extended as more views are extracted. */
export interface AppContextValue {
  phase: AppPhase;
  setPhase: (p: AppPhase) => void;
  isSignedIn: boolean;
  accessToken: string | null;
  educatorName: string;
  todayLabel: string;
  isOnline: boolean;
  isDarkMode: boolean;
  setIsDarkMode: (v: boolean) => void;
  syncStatus: 'idle' | 'ok' | 'error';
  navUsage: Record<string, number>;
  bumpNavUsage: (key: 'plan' | 'grade' | 'teach' | 'communicate') => void;
  /** Rolling 7-day shortcut telemetry + legacy nav counts. */
  recordShortcutUse: (key: HomeShortcutKey) => void;
  homeUrgentAlerts: HomeUrgentAlert[];
  /** Up to three “start here” actions on signed-in Home. */
  homeTopPriorityActions: HomeTopPriorityAction[];
  homeWeekInsights: HomeWeekInsights;
  homeShortcutOrder: HomeShortcutKey[];
  /** Open Schedule on Agenda, jump cursor to date, scroll/highlight the item row. */
  openScheduleToItem: (scheduleItemId: string, dateISO: string) => void;
  homeSummary: HomeSummary;
  handleShareApp: () => void;
  /** May revoke Google OAuth and sign out Firebase — await in tests if needed. */
  handleSignOut: () => void | Promise<void>;
  authError: string | null;
  authSuccessMessage: string | null;
  handleGoogleLogin: () => void;
  showMoreAuthOptions: boolean;
  setShowMoreAuthOptions: (v: boolean) => void;
  handleEmailLogin: (e: React.FormEvent) => void;
  handlePasswordReset: () => void;
  authMode: 'signin' | 'signup';
  setAuthMode: (mode: 'signin' | 'signup') => void;
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  fullName: string;
  setFullName: (v: string) => void;
  salutation: string;
  setSalutation: (v: string) => void;
  firstName: string;
  setFirstName: (v: string) => void;
  lastName: string;
  setLastName: (v: string) => void;
  /** Grading-only product shell (avatar menu, settings sheet). */
  gradingOnlyMode?: boolean;
  openGradingSettings?: () => void;
  openGradingBilling?: () => void;
  /** lg:hidden slide-over nav (replaces bottom tab bar on mobile). */
  showMobileShellNav?: boolean;
  mobileShellNavOpen?: boolean;
  setMobileShellNavOpen?: (open: boolean) => void;
}

export const AppContext = React.createContext<AppContextValue | null>(null);

export function useAppContext(): AppContextValue {
  const ctx = React.useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppContext.Provider');
  return ctx;
}
